import { useEffect } from "react";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Divider, Form, Input, InputNumber, Row, Select } from "antd";

import { useGetStatesOfCountry } from "services/catalogs.service";

import {
  CATALOG_PAYMENT_METHOD_MAP_OPTS,
  CATALOG_PAYMENT_TYPE_MAP_OPTS,
  CATALOG_PERIODICITY_MAP_OPTS,
  CATALOG_SERVICE_SUB_TYPE_MAP_OPTS,
  CATALOG_SERVICE_TYPE_MAP_OPTS,
  FISCAL_DOC_ISSUANCE_MODE,
} from "constants/taxes";

interface Props {
  nameForm: string;
  textSubmit?: string;
  loading?: boolean;
  initialValues?: any;
  resetFields?: boolean;
  hiddenSomeFields?: boolean;
  handleOnSubmit?: (values: any) => void;
}

export function NewFiscalDocForm(props: Props) {
  const {
    nameForm,
    textSubmit = "Enviar",
    loading = false,
    initialValues = {},
    resetFields = false,
    hiddenSomeFields = false,
    handleOnSubmit = () => undefined,
  } = props;

  const [form] = Form.useForm();
  const countryStates = useGetStatesOfCountry();

  const onFinish = (values): void => {
    const { conditions, specifications } = values;
    const newValues = {
      ...values,
      conditions: {
        needs_computation_approval: !!conditions.needs_computation_approval,
        needs_status_validation: !!conditions.needs_status_validation,
      },
    };
    newValues.specifications.period_from = specifications.period_range[0].format("YYYY-MM-DDT12:00:00") + "Z";
    newValues.specifications.period_to = specifications.period_range[1].format("YYYY-MM-DDT12:00:00") + "Z";
    delete newValues.specifications.period_range;
    handleOnSubmit(newValues);
  };

  useEffect((): void => {
    if (resetFields) form.resetFields();
  }, [resetFields]);

  return (
    <Form
      form={form}
      name={nameForm}
      layout="horizontal"
      labelCol={{ span: 9 }}
      onFinish={onFinish}
      onFinishFailed={null}
      initialValues={initialValues}
    >
      <Form.Item required label="customer_name" name={["customer_name"]} hidden={hiddenSomeFields}>
        <Input />
      </Form.Item>

      <Form.Item required label="ID" name={["taxpayer_id"]} hidden={hiddenSomeFields}>
        <Input disabled={true} />
      </Form.Item>

      <Form.Item required label="Tipo de documento" name={["document"]}>
        <Select disabled={true}>
          {Object.values(FISCAL_DOC_ISSUANCE_MODE).map((item) => (
            <Select.Option key={item.value} value={item.value}>
              {item.description}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item required label="Periodicidad" name={["specifications", "periodicity"]} hidden={hiddenSomeFields}>
        <Select>
          {CATALOG_PERIODICITY_MAP_OPTS.map((item) => (
            <Select.Option key={item.property} value={item.property}>
              {item.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Periodo Desde - Hasta"
        name={["specifications", "period_range"]}
        rules={[{ required: true, message: "Seleccione ambas fechas de inicio y final" }]}
      >
        <DatePicker.RangePicker format="YYYY-MM-DD" />
      </Form.Item>

      <Form.Item label="Numero de Servicios" name={["specifications", "service_count"]} hidden={hiddenSomeFields}>
        <InputNumber style={{ minWidth: "8rem" }} />
      </Form.Item>

      <Form.List name={["specifications", "services"]}>
        {(services, { add, remove }) => (
          <>
            <Divider />
            {services.map((service) => (
              <Row key={service.key} style={{ marginBottom: "0.75rem", borderBottom: "1px solid lightgray" }}>
                <Col span={21}>
                  <Form.Item
                    label="Estado Federativo"
                    name={[service.name, "state"]}
                    fieldKey={[service.fieldKey, "state"]}
                    rules={[{ required: true, message: "Selecciona un Estado del País" }]}
                  >
                    <Select>
                      {countryStates.data?.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    required
                    label="Tipo de pago"
                    name={[service.name, "payment_type"]}
                    fieldKey={[service.fieldKey, "payment_type"]}
                    initialValue={initialValues?.specifications?.services[0]?.payment_type}
                    hidden={hiddenSomeFields}
                  >
                    <Select>
                      {CATALOG_PAYMENT_TYPE_MAP_OPTS.map((item) => (
                        <Select.Option key={item.property} value={item.property}>
                          {item.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    required
                    label="Método de pago"
                    name={[service.name, "payment_method"]}
                    fieldKey={[service.fieldKey, "payment_method"]}
                    initialValue={initialValues?.specifications?.services[0]?.payment_method}
                    hidden={hiddenSomeFields}
                  >
                    <Select>
                      {CATALOG_PAYMENT_METHOD_MAP_OPTS.map((item) => (
                        <Select.Option key={item.property} value={item.property}>
                          {item.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Ingresos"
                    name={[service.name, "income"]}
                    fieldKey={[service.fieldKey, "income"]}
                    rules={[{ required: true, message: "Se requiere un valor de ingresos" }]}
                  >
                    <InputNumber min={0.0} style={{ minWidth: "8rem" }} />
                  </Form.Item>

                  <Form.Item label="Propinas" name={[service.name, "tips"]} fieldKey={[service.fieldKey, "tips"]}>
                    <InputNumber style={{ minWidth: "8rem" }} />
                  </Form.Item>

                  <Form.Item
                    label="Cuota de Servicio"
                    name={[service.name, "service_fee"]}
                    fieldKey={[service.fieldKey, "service_fee"]}
                    hidden={hiddenSomeFields}
                  >
                    <InputNumber style={{ minWidth: "8rem" }} />
                  </Form.Item>

                  <Form.Item
                    required
                    label="Tipo de Servicio"
                    name={[service.name, "service_type"]}
                    fieldKey={[service.fieldKey, "service_type"]}
                    initialValue={initialValues?.specifications?.services[0]?.service_type}
                    hidden={hiddenSomeFields}
                  >
                    <Select>
                      {CATALOG_SERVICE_TYPE_MAP_OPTS.map((item) => (
                        <Select.Option key={item.property} value={item.property}>
                          {item.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    required
                    label="Sub tipo de Servicio"
                    name={[service.name, "service_sub_type"]}
                    fieldKey={[service.fieldKey, "service_sub_type"]}
                    initialValue={initialValues?.specifications?.services[0]?.service_sub_type}
                    hidden={hiddenSomeFields}
                  >
                    <Select>
                      {CATALOG_SERVICE_SUB_TYPE_MAP_OPTS.map((item) => (
                        <Select.Option key={item.property} value={item.property}>
                          {item.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Contribución Gubernamental"
                    name={[service.name, "government_contribution"]}
                    fieldKey={[service.fieldKey, "government_contribution"]}
                    hidden={hiddenSomeFields}
                  >
                    <InputNumber style={{ minWidth: "8rem" }} />
                  </Form.Item>
                </Col>

                <Col
                  span={2}
                  offset={1}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    borderLeft: "1px solid lightgray",
                  }}
                >
                  <MinusCircleOutlined onClick={() => remove(service.name)} style={{ fontSize: "1.25rem" }} />
                </Col>
              </Row>
            ))}

            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Agregar más servicios
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <Form.Item
        required
        label="needs_computation_approval"
        name={["conditions", "needs_computation_approval"]}
        hidden={hiddenSomeFields}
      >
        <Select>
          <Select.Option value={0}>NO</Select.Option>
          <Select.Option value={1}>SI</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        required
        label="needs_status_validation"
        name={["conditions", "needs_status_validation"]}
        hidden={hiddenSomeFields}
      >
        <Select>
          <Select.Option value={0}>NO</Select.Option>
          <Select.Option value={1}>SI</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {textSubmit}
        </Button>
      </Form.Item>
    </Form>
  );
}
